import React, { CSSProperties, memo, useCallback, useMemo } from 'react'
import Condition from 'components/Condition'
import { Badge, Button, IconButton, Tooltip } from '@mui/material'
import { Chat } from '@mui/icons-material'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { ChatButton_Recipient$key } from '__generated__/ChatButton_Recipient.graphql'
import { useChat } from 'hooks/chat'
import { ChatButton_ChatThread$key } from '__generated__/ChatButton_ChatThread.graphql'

interface IChatButtonProps {
  chatThreadFragmentRef?: ChatButton_ChatThread$key | null
  recipientFragmentRef?: ChatButton_Recipient$key | null
  showContactInfo?: boolean
  showMessageCount?: boolean
  color?: 'primary' | 'secondary'
  variant?: 'contained' | 'outlined' | 'text'
  style?: CSSProperties
  iconOnly?: boolean
  label?: string
  defaultMessage?: string
}

function ChatButton(props: IChatButtonProps) {
  const {
    recipientFragmentRef,
    chatThreadFragmentRef,
    showContactInfo,
    color,
    variant,
    style,
    iconOnly,
    label: labelProp,
    defaultMessage,
    showMessageCount = true,
  } = props
  const { openChat } = useChat()

  const recipient = useFragment(graphql`
    fragment ChatButton_Recipient on User {
      id
      firstName
      self

      directChatThread {
        ...ChatButton_ChatThread
      }
    }
  `, recipientFragmentRef)

  const chatThread = useFragment(graphql`
    fragment ChatButton_ChatThread on ChatThread {
      id

      unreadMessages: chatMessages(filters: { unread: true }) {
        totalCount
      }

      chatMessages {
        totalCount
      }
    }
  `, (chatThreadFragmentRef || recipient?.directChatThread) as ChatButton_ChatThread$key)

  const messagesCount = showMessageCount ? (chatThread?.chatMessages?.totalCount || 0) : 0
  const unreadMessagesCount = showMessageCount ? (chatThread?.unreadMessages?.totalCount || 0) : 0

  const open = useCallback(() => {
    openChat({
      id: chatThread?.id,
      recipientId: chatThread?.id ? undefined : recipient?.id,
      defaultMessage: defaultMessage,
    })
  }, [openChat, chatThread?.id, recipient?.id, defaultMessage])

  const label = useMemo(() => (
    labelProp || (recipient?.firstName && showContactInfo ? `Chat with ${recipient?.firstName}` : 'Chat')
  ), [labelProp, showContactInfo, recipient])

  if (!recipient || recipient?.self) return null

  const fixedColor = color === 'secondary' ? 'primary' : color
  const icon = <Chat />

  // TODO: add UI to show online status with subscription for real time update

  return (
    <Badge
      color="error"
      badgeContent={unreadMessagesCount}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      overlap={iconOnly ? 'circular' : undefined}
      style={style}
    >
      <Condition condition={iconOnly}>
        <Tooltip title="Start chat">
          <IconButton color={fixedColor || 'primary'} onClick={open}>
            {icon}

            <Condition condition={!iconOnly}>
              {label} {messagesCount > 0 && `(${messagesCount})`}
            </Condition>
          </IconButton>
        </Tooltip>
      </Condition>

      <Condition condition={!iconOnly}>
        <Button color={fixedColor || 'secondary'} onClick={open} variant={variant} startIcon={icon}>
          <Condition condition={!iconOnly}>
            {label} {messagesCount > 0 && `(${messagesCount})`}
          </Condition>
        </Button>
      </Condition>
    </Badge>
  )
}

export default memo(ChatButton)
